import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  ACCOUNT_MISMATCH_EVENT,
  ApiError,
  api,
  AUTH_REQUIRED_EVENT,
  haltRequests,
  SESSION_LOCKED_EVENT,
  sessionRefused,
} from "../api/client";
import type { AuthSession, LicenceStatus } from "../api/types";
import { Banner, Callout } from "../components/Callout";
import { Dialog, DialogBody } from "../components/Dialog";
import { GenerexMark } from "../components/GenerexMark";
import { Motion, Presence } from "../components/presence";
import { Button } from "../components/ui";
import { showAccountsHelp } from "../help/topics";
import { t } from "../i18n/i18n";
import { NoticeText, PrivacyConsent } from "../licence/PrivacyConsent";
import { licenceScope } from "../state/licenceScope";
import { useUi } from "../state/ui";
import { unsavedDrafts } from "../state/unsavedDrafts";
import { drainOrderEditors, flushOrderQueues, haltOrderQueues, retryOrderQueues } from "../views/order/orderSaveQueue";
import {
  appLeaveDeps,
  AUTH_SESSION_QUERY,
  chooseLater,
  laterChosen,
  leaveSession,
  readSession,
  reloadUiState,
  setSessionFlag,
  SIGNED_OUT_KEY,
  unsavedLabels,
  useAuth,
} from "./auth";
import { AuthChannel, collectOtherTabs, reactionTo, TAB_ID } from "./authChannel";
import { ChangePasswordDialog, hasSinceLines, MyEventsDialog, MySessionsDialog, SignOutDialog, SinceLastLoginDialog } from "./AccountDialogs";
import { AccountsView } from "./admin/AccountsView";
import { ReauthHost } from "./admin/AdminDialogs";
import {
  accountsRelevant,
  authView,
  type BareApp,
  bareApp,
  bootstrapOffer,
  mountedFrom,
  type OverlayName,
  overlayStep,
  refusalTaken,
  serverHintStale,
  type SignInScreen,
} from "./authState";
import { BootstrapOffer, BootstrapScreen } from "./BootstrapScreen";
import { type IdleTracker, installIdle } from "./idle";
import { LockOverlay, SwitchUserConfirm } from "./LockOverlay";
import { LoginScreen } from "./LoginScreen";
import { setMountedScope } from "./mounted";
import { RecoveryCodeStep } from "./RecoveryCodeStep";
import { RecoverScreen, RedeemScreen } from "./RedeemScreen";
import { Waiting } from "../components/Waiting";

/** Version de la notice de protection des données acceptée par chaque compte (docs/17 §2.2). */
const NOTICE_VERSION = 3;

/** Portail des comptes nominatifs (docs/17 §4, §8.1), placé sous `LicenceGate`.
 *
 *  - Licence en mode hérité (jamais basculée, serveur sans comptes) : l'application telle qu'aujourd'hui, sans aucune
 *    requête de plus.
 *  - Licence en mode comptes : proposition de bascule, compte administrateur et code de secours, connexion, code
 *    d'activation ou de réinitialisation, notice, puis l'application, montée pour un compte dont l'identité ne change
 *    plus (en-tête `X-Generex-Compte` sur chaque requête, brouillons sous sa portée).
 *  - Verrouillage (inactivité partagée entre onglets, menu, serveur), expiration à 12 h, session fermée : surcouche
 *    PAR-DESSUS l'application, rendue inerte, à identité fixe ; les saisies sont d'abord recopiées dans le navigateur
 *    et les files ne repartent que pour la même personne.
 *  - « Changer d'utilisateur », session ouverte pour un autre compte (409, autre onglet) : rangement des saisies,
 *    arrêt des files, rechargement complet ; aucune requête de A ne part sous la session de B. */
export function AuthGate({ licence, children }: { licence: LicenceStatus; children: ReactNode }) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const relevant = accountsRelevant(licence);
  const query = useQuery({
    queryKey: AUTH_SESSION_QUERY,
    queryFn: readSession,
    enabled: relevant,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: relevant,
  });
  const session = query.data;
  const mounted = useAuth((s) => s.mounted);
  const hint = useAuth((s) => s.hint);
  const lockedAt = useAuth((s) => s.lockedAt);
  const dialog = useAuth((s) => s.dialog);
  const since = useAuth((s) => s.since);
  const [later, setLater] = useState(laterChosen);
  const [screen, setScreen] = useState<SignInScreen>("login");
  const [bootStep, setBootStep] = useState<"offer" | "form">("offer");
  const [recovery, setRecovery] = useState<string | null>(null);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [leavingText, setLeavingText] = useState<string | null>(null);
  const [askSwitch, setAskSwitch] = useState(false);
  const [signOut, setSignOut] = useState<{ labels: string[]; busy: boolean } | null>(null);
  const draftsMoved = useRef(false);

  const { view, overlay } = authView({ licence, session, mounted, hint, later, screen, recovery: recovery !== null });

  // Application rendue sans compte dans ce document (mode hérité, repli) : elle le reste jusqu'au rechargement. Un
  // code d'amorçage émis en cours de travail attend l'ouverture suivante (§8.3 : la proposition suit « Accéder à
  // Generex ») ; des comptes ouverts depuis un autre poste font ranger les saisies et recharger (§4.8), jamais
  // remplacer l'application par l'écran de connexion.
  const bare = useRef<BareApp | null>(null);
  const accountsMode = licence.accounts?.mode ?? "legacy";
  const bareState = bareApp(bare.current, { mounted: mounted !== null, relevant, view, mode: accountsMode });
  bare.current = bareState.bare;
  const shownBare = bareState.shown;
  // Ce que montre ce document, pour les autres onglets : l'application rendue sans compte compte comme l'application.
  const viewRef = useRef(view);
  viewRef.current = shownBare ?? view;

  // Canal entre onglets : seulement pour une licence en mode comptes (mode hérité : rien de nouveau). Ouvert et fermé
  // par un effet (le double passage des effets en développement ne laisse pas un canal fermé en service).
  const [channel, setChannel] = useState(() => new AuthChannel(() => null));
  useEffect(() => {
    if (!relevant) return;
    const opened = new AuthChannel();
    setChannel(opened);
    return () => {
      opened.close();
      setChannel(new AuthChannel(() => null));
    };
  }, [relevant]);
  // Fiches d'aide des comptes : seulement pour une licence qui a affaire aux comptes (mode hérité : l'aide d'aujourd'hui).
  useEffect(() => {
    showAccountsHelp(relevant);
  }, [relevant]);
  const refetchSession = useCallback(() => void queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY }), [queryClient]);

  // --- Montage pour un compte : portée des brouillons, état de l'interface, en-tête du compte ---------------------
  useLayoutEffect(() => {
    if (view !== "app" || mounted || !session) return;
    const next = mountedFrom(session);
    if (!next) return;
    // Sans référence de licence (serveur ancien), la portée de la personne tient lieu de licence : jamais celle d'un
    // autre.
    const { result, drafts } = licenceScope.adoptAccount(next.scope_ref, licence.space_ref ?? next.scope_ref);
    if (result !== "same") reloadUiState();
    draftsMoved.current = drafts > 0;
    setMountedScope(next.scope_ref);
    useAuth.setState({ mounted: next, appointment: session.account?.appointment ?? null, admin: session.account?.role === "admin" });
  }, [view, mounted, session, licence.space_ref]);

  // Après le montage : brouillons repris de la licence (§6.4), « Depuis votre dernière connexion ».
  useEffect(() => {
    if (!mounted) return;
    if (draftsMoved.current) {
      draftsMoved.current = false;
      notify({ text: t("Saisies non enregistrées de ce poste reprises"), tone: "info" });
    }
    if (hasSinceLines(useAuth.getState().since)) useAuth.getState().openDialog("since");
  }, [mounted, notify]);

  // Fonction et rôle du compte monté, tels que la session les rend.
  useEffect(() => {
    const account = session?.account;
    if (mounted && account && account.id === mounted.id) useAuth.setState({ appointment: account.appointment ?? null, admin: account.role === "admin" });
  }, [session, mounted]);

  // --- Surcouche : saisies recopiées à son apparition (comme pour la licence perdue, audit M6) ------------------
  const shownOverlay = useRef<OverlayName | null>(null);
  useEffect(() => {
    const step = overlayStep(shownOverlay.current, overlay);
    shownOverlay.current = overlay;
    if (step === "snapshot") {
      drainOrderEditors();
      unsavedDrafts.snapshotAll();
      // Confirmations du menu du compte restées ouvertes : elles passeraient sous la surcouche.
      setAskSwitch(false);
      setSignOut((current) => (current?.busy ? current : null));
    }
  }, [overlay]);

  // --- Départs de la session ------------------------------------------------------------------------------------
  const leaving = useRef(false);
  /** Changer d'utilisateur (`switch`, avec déconnexion), ou session de ce navigateur ouverte pour un autre compte
   *  (`other` : 409, connexion dans un autre onglet), sans rien rejouer. */
  const leave = useCallback(
    (kind: "switch" | "other") => {
      if (leaving.current) return;
      leaving.current = true;
      const current = useAuth.getState().mounted;
      setAskSwitch(false);
      setLeavingText(
        kind === "switch"
          ? t("Changement d'utilisateur : Generex se recharge.")
          : t("Session changée dans un autre onglet : Generex se recharge. Les saisies non enregistrées de {nom} restent sur ce poste, à son nom.", {
              nom: current?.display_name ?? "—",
            }),
      );
      void leaveSession(
        appLeaveDeps({
          clearCache: () => queryClient.clear(),
          broadcast: () => {
            if (kind === "switch") channel.post({ kind: "switch", scope_ref: current?.scope_ref ?? null });
          },
          reloadAfterMs: kind === "other" ? 1500 : 0,
        }),
        { logout: kind === "switch" },
      );
    },
    [channel, queryClient],
  );

  // Application montée pour un compte (ou sans compte) qui doit remonter sur une autre session.
  const reloadForOther = useCallback(() => {
    if (useAuth.getState().mounted) return leave("other");
    drainOrderEditors();
    unsavedDrafts.snapshotAll();
    haltOrderQueues();
    haltRequests();
    licenceScope.freeze();
    unsavedDrafts.close();
    queryClient.clear();
    window.location.reload();
  }, [leave, queryClient]);

  // Comptes ouverts (ou rétablis) sous une application rendue sans compte : rangement, puis rechargement complet. Le
  // mode lu à l'ouverture compte : une licence déjà en mode comptes au chargement ne recharge pas en boucle.
  const bareReload = bareState.reload;
  useEffect(() => {
    if (bareReload) reloadForOther();
  }, [bareReload, reloadForOther]);

  // Verrouillage : inactivité, menu du compte, autre onglet. La surcouche tient même si le serveur est injoignable.
  const lockNow = useCallback(() => {
    const current = useAuth.getState().mounted;
    if (!current) return;
    useAuth.getState().setHint("locked");
    channel.post({ kind: "lock", scope_ref: current.scope_ref });
    void api.auth
      .lock()
      .catch(() => undefined)
      .finally(refetchSession);
  }, [channel, refetchSession]);

  // Déconnexion volontaire : ce qui attend part d'abord (cet onglet et les autres du même compte), puis
  // avertissement s'il reste des saisies, puis effacement des brouillons de la personne, déconnexion, rechargement.
  const finishSignOut = useCallback(async () => {
    const current = useAuth.getState().mounted;
    setSignOut((s) => (s ? { ...s, busy: true } : s));
    try {
      await api.auth.logout();
    } catch (error) {
      if (error instanceof ApiError && error.status === 0) {
        setSignOut(null);
        notify({ text: t("Déconnexion impossible"), detail: error.message, tone: "error" });
        return;
      }
    }
    setSessionFlag(SIGNED_OUT_KEY, true);
    leaving.current = true;
    const deps = appLeaveDeps({
      clearCache: () => queryClient.clear(),
      broadcast: () => channel.post({ kind: "logout", scope_ref: current?.scope_ref ?? null }),
      erase: true,
    });
    await leaveSession({ ...deps, drain: () => undefined, snapshot: () => undefined }, { logout: false });
  }, [channel, notify, queryClient]);

  const startSignOut = useCallback(async () => {
    const current = useAuth.getState().mounted;
    if (!current) return;
    const [others] = await Promise.all([collectOtherTabs(channel, current.scope_ref), flushOrderQueues(4000)]);
    unsavedDrafts.snapshotAll();
    const labels = [...new Set([...unsavedLabels(), ...others])];
    if (labels.length || licenceScope.storedDrafts() > 0) setSignOut({ labels, busy: false });
    else void finishSignOut();
  }, [channel, finishSignOut]);

  useEffect(() => {
    useAuth.setState({ actions: { lock: lockNow, switchUser: () => setAskSwitch(true), signOut: () => void startSignOut() } });
  }, [lockNow, startSignOut]);

  // Retour de la même personne (déverrouillage, reconnexion par la surcouche ou dans un autre onglet).
  const resumedAt = useRef(0);
  const resumeSame = useCallback(() => {
    resumedAt.current = Date.now();
    useAuth.getState().setHint(null);
    retryOrderQueues();
    // Lectures refusées pendant la surcouche (vue ouverte dessous, relectures automatiques) : relues pour la même
    // personne, sans attendre une action (un refus de session n'est jamais réessayé de lui-même).
    void queryClient.refetchQueries({ predicate: (q) => q.state.status === "error" && sessionRefused(q.state.error) });
  }, [queryClient]);

  // --- Refus des requêtes de l'application montée -------------------------------------------------------------
  useEffect(() => {
    if (!mounted) return;
    // Refus d'une requête partie avant la dernière reprise (relecture en vol pendant le déverrouillage) : ignoré.
    // Surcouche déjà posée : rien de plus (les relectures automatiques continuent dessous, sans relire la session à
    // chaque refus).
    const refuse = (event: Event, next: OverlayName) => {
      const sent = Number((event as CustomEvent<{ sent_at?: unknown } | undefined>).detail?.sent_at ?? 0) || 0;
      if (!refusalTaken(sent, resumedAt.current, useAuth.getState().hint, next)) return;
      useAuth.getState().setHint(next, true);
      refetchSession();
    };
    const onLocked = (event: Event) => refuse(event, "locked");
    const onRequired = (event: Event) => {
      const reason = (event as CustomEvent<{ reason?: unknown } | undefined>).detail?.reason;
      refuse(event, reason === "expired" ? "expired" : "closed");
    };
    const onMismatch = () => leave("other");
    window.addEventListener(SESSION_LOCKED_EVENT, onLocked);
    window.addEventListener(AUTH_REQUIRED_EVENT, onRequired);
    window.addEventListener(ACCOUNT_MISMATCH_EVENT, onMismatch);
    return () => {
      window.removeEventListener(SESSION_LOCKED_EVENT, onLocked);
      window.removeEventListener(AUTH_REQUIRED_EVENT, onRequired);
      window.removeEventListener(ACCOUNT_MISMATCH_EVENT, onMismatch);
    };
  }, [mounted, leave, refetchSession]);

  // Surcouche posée par un refus du serveur alors que la session, relue après lui, est ouverte pour la même personne
  // (refus tardif d'une requête en vol) : levée, files relancées. Une surcouche posée par ce navigateur reste.
  const sessionReadAt = query.dataUpdatedAt;
  useEffect(() => {
    if (serverHintStale(useAuth.getState(), session, sessionReadAt, mounted?.id ?? null)) resumeSame();
  }, [session, sessionReadAt, mounted, resumeSame]);

  // Session ouverte pour un autre compte, lue par la session elle-même : même chemin, sans rien rejouer.
  useEffect(() => {
    if (view === "switch") leave("other");
  }, [view, leave]);

  // --- Inactivité, partagée entre les onglets ---------------------------------------------------------------------
  const tracker = useRef<IdleTracker | null>(null);
  const lockMinutes = session?.lock_minutes && session.lock_minutes > 0 ? session.lock_minutes : 30;
  const blocked = view === "app" && overlay !== null;
  useEffect(() => {
    if (!mounted || blocked) return;
    const idle = installIdle(
      lockMinutes * 60_000,
      (at) => channel.post({ kind: "activity", scope_ref: mounted.scope_ref, at }),
      lockNow,
    );
    tracker.current = idle.tracker;
    return () => {
      idle.dispose();
      tracker.current = null;
    };
  }, [mounted, blocked, lockMinutes, channel, lockNow]);

  // --- Autres onglets ---------------------------------------------------------------------------------------------
  useEffect(
    () =>
      channel.listen((message) => {
        const current = useAuth.getState().mounted;
        const shown = viewRef.current;
        const shows = shown === "app" || shown === "legacy" || shown === "fallback" ? "app" : "screen";
        switch (reactionTo(message, current?.scope_ref ?? null, shows)) {
          case "activity":
            if (message.kind === "activity") tracker.current?.remote(message.at);
            break;
          case "resume":
            resumeSame();
            refetchSession();
            break;
          case "lock":
            useAuth.getState().setHint("locked");
            refetchSession();
            break;
          case "flush":
            if (message.kind !== "logout-pending") break;
            channel.post({ kind: "flush-start", id: message.id, tab: TAB_ID });
            void flushOrderQueues(3500).then(() => {
              unsavedDrafts.snapshotAll();
              channel.post({ kind: "flush-done", id: message.id, tab: TAB_ID, unsaved: unsavedLabels() });
            });
            break;
          case "erase":
            // Déconnexion de la personne dans un autre onglet : ses brouillons quittent ce navigateur.
            leaving.current = true;
            haltOrderQueues();
            haltRequests();
            licenceScope.signOut();
            licenceScope.freeze();
            unsavedDrafts.close();
            queryClient.clear();
            window.location.reload();
            break;
          case "reload":
            if (shows === "screen" && !current) refetchSession();
            else reloadForOther();
            break;
        }
      }),
    [channel, queryClient, refetchSession, reloadForOther, resumeSame],
  );

  // --- Connexions -----------------------------------------------------------------------------------------------
  /** Session ouverte par un écran (connexion, code, amorçage, récupération) : session relue, diffusée aux onglets. */
  const signedIn = useCallback(
    async (result: AuthSession) => {
      if (result.recovery_code) setRecovery(result.recovery_code);
      useAuth.setState({ since: result.since_last_login ?? [] });
      setScreen("login");
      try {
        const fresh = await readSession();
        queryClient.setQueryData(AUTH_SESSION_QUERY, fresh);
        channel.post({ kind: "login", scope_ref: fresh?.scope_ref ?? null });
      } catch {
        refetchSession();
      }
    },
    [channel, queryClient, refetchSession],
  );

  /** Reprise par la surcouche (déverrouillage, reconnexion de la même personne). Autre compte (cas qui ne doit pas
   *  arriver) : chemin « Changer d'utilisateur », sans rien rejouer. */
  const resumed = useCallback(
    async (result: AuthSession, mode: OverlayName) => {
      const current = useAuth.getState().mounted;
      let fresh: AuthSession | null = null;
      try {
        fresh = await readSession();
      } catch {
        fresh = null;
      }
      const id = fresh?.account?.id ?? result.account?.id ?? null;
      if (current && id && id !== current.id) return leave("other");
      if (fresh) queryClient.setQueryData(AUTH_SESSION_QUERY, fresh);
      else refetchSession();
      resumeSame();
      channel.post({ kind: mode === "locked" ? "unlock" : "login", scope_ref: current?.scope_ref ?? null });
      notify({
        text: mode === "locked" ? t("Session déverrouillée") : t("Session reprise"),
        detail: t("Vos saisies en cours sont intactes."),
        tone: "ok",
      });
      const events = result.since_last_login ?? [];
      if (events.length) {
        useAuth.setState({ since: events });
        if (hasSinceLines(events)) useAuth.getState().openDialog("since");
      }
    },
    [channel, leave, notify, queryClient, refetchSession, resumeSame],
  );

  // Écran par-dessus l'application : le clavier ne l'atteint plus (comme la surcouche de licence).
  const overlayBox = useRef<HTMLDivElement>(null);
  const covered = blocked || leavingText !== null || view === "switch";
  useEffect(() => {
    if (!covered) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || !overlayBox.current?.contains(e.target as Node)) e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [covered]);

  // --- Rendu --------------------------------------------------------------------------------------------------
  const closeDialog = () => useAuth.getState().openDialog(null);

  // Application montée pour un compte : elle le reste jusqu'au rechargement, sous sa surcouche s'il y a lieu.
  if ((view === "app" || view === "switch") && mounted) {
    const identity = mounted;
    return (
      <>
        {/* Sans boîte (`contents`) : la mise en page de l'application est inchangée ; inerte sous la surcouche. */}
        <div className="contents" inert={covered} aria-hidden={covered || undefined}>
          {children}
          {/* Fenêtres du menu du compte : au-dessus de l'accueil (plan z-60), d'où s'ouvre aussi le menu. */}
          <div className="relative z-[61]">
            <Presence show={dialog === "password"}>{dialog === "password" && <ChangePasswordDialog onClose={closeDialog} />}</Presence>
            <Presence show={dialog === "sessions"}>{dialog === "sessions" && <MySessionsDialog onClose={closeDialog} />}</Presence>
            <Presence show={dialog === "events"}>{dialog === "events" && <MyEventsDialog onClose={closeDialog} />}</Presence>
            <Presence show={dialog === "since"}>{dialog === "since" && <SinceLastLoginDialog events={since} onClose={closeDialog} />}</Presence>
            {/* Comptes de l'unité (administrateurs, lot 4) et confirmation du mot de passe des gestes d'administration. */}
            <Presence show={dialog === "accounts"}>{dialog === "accounts" && <AccountsView onClose={closeDialog} />}</Presence>
            <ReauthHost />
            <Presence show={askSwitch}>
              {askSwitch && <SwitchUserConfirm name={identity.display_name} onConfirm={() => leave("switch")} onCancel={() => setAskSwitch(false)} />}
            </Presence>
            <Presence show={signOut !== null}>
              {signOut && (
                <SignOutDialog labels={signOut.labels} busy={signOut.busy} onConfirm={() => void finishSignOut()} onCancel={() => setSignOut(null)} />
              )}
            </Presence>
          </div>
        </div>
        <Presence show={covered}>
          {covered && (
            // Juste sous la surcouche de licence (z-95), au-dessus des fenêtres et confirmations de l'application.
            <Motion
              ref={overlayBox}
              origin="fade"
              className="absolute inset-0 z-[94] overflow-y-auto bg-canvas"
              onKeyDown={(e) => e.stopPropagation()}
            >
              {leavingText !== null || view === "switch" ? (
                <ReloadingScreen text={leavingText ?? t("Session changée dans un autre onglet : Generex se recharge.")} />
              ) : (
                overlay && (
                  <LockOverlay
                    key={overlay}
                    mode={overlay}
                    identity={identity}
                    lockedAt={session?.locked_at ?? lockedAt}
                    onResumed={(result) => void resumed(result, overlay)}
                    onSwitch={() => leave("switch")}
                  />
                )
              )}
            </Motion>
          )}
        </Presence>
      </>
    );
  }

  if (shownBare === "legacy") return <>{children}</>;
  if (shownBare === "fallback") return <FallbackFrame>{children}</FallbackFrame>;

  if (query.isError && !mounted) {
    return (
      <Motion origin="fade" className="flex min-h-full flex-col items-center justify-center gap-4 bg-canvas p-6 text-center">
        <GenerexMark size={40} />
        <Callout tone="danger" className="max-w-[360px] text-left">
          {query.error instanceof ApiError && query.error.status !== 0
            ? query.error.message
            : t("Le serveur Generex ne répond pas. Vérifiez la connexion du poste, puis réessayez.")}
        </Callout>
        <Button variant="secondary" onClick={() => void query.refetch()} disabled={query.isFetching}>
          {t("Réessayer")}
        </Button>
      </Motion>
    );
  }

  switch (view) {
    case "recovery":
      return recovery ? (
        <RecoveryCodeStep
          code={recovery}
          onDone={() => {
            setRecovery(null);
            refetchSession();
          }}
        />
      ) : null;
    case "bootstrap": {
      const required = bootstrapOffer(session, licence) === "required";
      return (
        <>
          {bootStep === "offer" ? (
            <BootstrapOffer
              required={required}
              onCreate={() => setBootStep("form")}
              onLater={() => {
                chooseLater();
                setLater(true);
              }}
            />
          ) : (
            <BootstrapScreen
              onDone={(result) => void signedIn(result)}
              onBack={() => setBootStep("offer")}
              onTaken={refetchSession}
              showNotice={() => setNoticeOpen(true)}
            />
          )}
          <Presence show={noticeOpen}>
            {noticeOpen && (
              <Dialog title={t("Protection des données personnelles")} width={640} onClose={() => setNoticeOpen(false)}>
                <DialogBody>
                  <NoticeText version={NOTICE_VERSION} accounts className="space-y-3.5" />
                </DialogBody>
              </Dialog>
            )}
          </Presence>
        </>
      );
    }
    case "login":
      return session ? <LoginScreen session={session} licence={licence} onDone={(result) => void signedIn(result)} onScreen={setScreen} /> : null;
    case "redeem":
      return session ? (
        <RedeemScreen session={session} licence={licence} onDone={(result) => void signedIn(result)} onBack={() => setScreen("login")} />
      ) : null;
    case "recover":
      return session ? (
        <RecoverScreen session={session} licence={licence} onDone={(result) => void signedIn(result)} onBack={() => setScreen("login")} />
      ) : null;
    case "notice":
      return (
        <PrivacyConsent
          version={NOTICE_VERSION}
          onAccept={async () => {
            await api.auth.notice();
            refetchSession();
          }}
        />
      );
    case "locked": {
      const identity = mountedFrom(session);
      return identity ? (
        <LockOverlay
          mode="locked"
          identity={identity}
          lockedAt={session?.locked_at ?? null}
          onResumed={() => {
            channel.post({ kind: "unlock", scope_ref: identity.scope_ref });
            refetchSession();
          }}
          onSwitch={() => {
            leaving.current = true;
            void leaveSession(
              appLeaveDeps({ clearCache: () => queryClient.clear(), broadcast: () => channel.post({ kind: "switch", scope_ref: identity.scope_ref }) }),
            );
          }}
        />
      ) : null;
    }
    case "switch":
      return <ReloadingScreen text={leavingText ?? t("Session changée dans un autre onglet : Generex se recharge.")} />;
    default:
      return <WaitScreen />;
  }
}

/** Repli du serveur (comptes suspendus) : bandeau au-dessus de l'application, rendue sans compte. */
function FallbackFrame({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <Banner tone="warn">
        {t("Comptes momentanément suspendus sur ce serveur : consultation seule des dossiers de l'unité. Les dossiers privés réapparaîtront au retour des comptes.")}
      </Banner>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

/** Attente de la session (premier chargement, montage). */
function WaitScreen() {
  return (
    <Motion origin="fade" className="flex min-h-full flex-col items-center justify-center gap-4 bg-canvas p-6 text-center">
      <GenerexMark size={40} />
      <p className="text-ui text-ink-muted">{<Waiting>{t("Ouverture de la session…")}</Waiting>}</p>
    </Motion>
  );
}

/** Rechargement annoncé (changement d'utilisateur, session changée dans un autre onglet). */
function ReloadingScreen({ text }: { text: string }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-canvas p-6 text-center">
      <GenerexMark size={40} />
      <p className="max-w-[420px] text-ui leading-relaxed text-ink-soft">{text}</p>
    </div>
  );
}
